import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BackButton, PrimaryButton } from 'components/forms/atoms/FormButtons';
import FormField from 'components/forms/atoms/FormField';
import FormSection from 'components/forms/atoms/FormSection';
import PhoneField from 'components/forms/atoms/PhoneField';
import FormGrid from 'components/forms/molecules/FormGrid';
import InlineFeedback from 'components/forms/molecules/InlineFeedback';
import { useFeedback } from 'contexts/FeedbackContext';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type AdminOnboardingRequest,
  type AdminOnboardingType,
  useCreateAdminOnboardingBusinessUnitMutation,
  useGetAdminCompaniesQuery,
} from 'services/api/adminOnboarding.api';
import { NAVY, RADIUS_CARD, RADIUS_ITEM, SHADOW, SURFACE, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

const TYPE_OPTIONS: { value: AdminOnboardingType; label: string; helper: string }[] = [
  { value: 'car_dealership', label: 'Concesionaria', helper: 'Venta de autos nuevos y usados' },
  { value: 'insurance_broker', label: 'Corredora de Seguros', helper: 'Cotización y venta de pólizas' },
  { value: 'financial_institution', label: 'Banco', helper: 'Productos financieros y bancarios' },
  { value: 'after_sales', label: 'Post-venta', helper: 'Agenda y atención post-venta' },
];

const parseDomains = (raw: string): string[] =>
  raw
    .split(/[,\n]/)
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);

const normalizePhone = (raw: string): string => raw.replace(/[+\s]/g, '');

interface PendingErrors {
  businessUnitName?: string;
  type?: string;
  companyName?: string;
  companyId?: string;
}

const AdminOnboarding: FC = () => {
  const navigate = useNavigate();
  const { notifySaved } = useFeedback();
  const [createOnboarding, { isLoading }] = useCreateAdminOnboardingBusinessUnitMutation();

  const [businessUnitName, setBusinessUnitName] = useState('');
  const [businessUnitPhoneNumber, setBusinessUnitPhoneNumber] = useState('');
  const [type, setType] = useState<AdminOnboardingType>('car_dealership');
  const [existingCompany, setExistingCompany] = useState(false);

  const isDev = false;
  // const isDev = config.environment === 'development';

  const [companyName, setCompanyName] = useState('');
  const [domainsRaw, setDomainsRaw] = useState('');
  const [whatsappGroupJid, setWhatsappGroupJid] = useState('');
  const [setupNotion, setSetupNotion] = useState(!isDev);

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [scaffoldNotionForBu, setScaffoldNotionForBu] = useState(!isDev);

  const [errors, setErrors] = useState<PendingErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: companies = [], isFetching: isLoadingCompanies } = useGetAdminCompaniesQuery(undefined, {
    skip: !existingCompany,
  });

  const sortedCompanies = useMemo(
    () => [...companies].sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [companies]
  );

  const selectedCompany = useMemo(
    () => sortedCompanies.find((c) => c.id === selectedCompanyId) ?? null,
    [sortedCompanies, selectedCompanyId]
  );

  const selectedCompanyHasNotion = !!selectedCompany?.notionConfig?.businessUnitsDbId;
  const scaffoldDisabledByMissingNotion = existingCompany && !!selectedCompany && !selectedCompanyHasNotion;
  const scaffoldDisabled = isDev || scaffoldDisabledByMissingNotion;
  const effectiveScaffold = scaffoldDisabled ? false : scaffoldNotionForBu;
  const effectiveSetupNotion = isDev ? false : setupNotion;
  const scaffoldHelper = isDev
    ? 'Deshabilitado en development.'
    : scaffoldDisabledByMissingNotion
      ? 'La compañía no tiene Notion configurado, se omite.'
      : 'Agrega la BU al Notion de la compañía y guarda la referencia en companyNotionConfig.';
  const setupNotionHelper = isDev
    ? 'Deshabilitado en development.'
    : 'Crea la página del cliente, los databases y la página de la BU.';

  const validate = (): PendingErrors => {
    const e: PendingErrors = {};
    if (!businessUnitName.trim()) e.businessUnitName = 'Falta el nombre de la BU';
    if (!type) e.type = 'Selecciona un tipo';
    if (!existingCompany && !companyName.trim()) e.companyName = 'Falta el nombre de la compañía';
    if (existingCompany && !selectedCompanyId) e.companyId = 'Selecciona una compañía';
    return e;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      setSubmitError('Revisa los campos marcados antes de continuar.');
      return;
    }

    const payload: AdminOnboardingRequest = existingCompany
      ? {
          mode: 'existingCompany',
          businessUnitName: businessUnitName.trim(),
          businessUnitPhoneNumber: normalizePhone(businessUnitPhoneNumber) || undefined,
          type,
          companyId: selectedCompanyId as number,
          scaffoldNotionForBu: effectiveScaffold,
        }
      : {
          mode: 'newCompany',
          businessUnitName: businessUnitName.trim(),
          businessUnitPhoneNumber: normalizePhone(businessUnitPhoneNumber) || undefined,
          type,
          company: {
            name: companyName.trim(),
            domains: parseDomains(domainsRaw).length ? parseDomains(domainsRaw) : undefined,
            whatsappGroupJid: whatsappGroupJid.trim() || undefined,
          },
          setupNotion: effectiveSetupNotion,
        };

    try {
      const res = await createOnboarding(payload).unwrap();
      notifySaved('BU creada');
      const flags: string[] = [];
      if (res.notion.setupRan) flags.push('Notion configurado');
      if (res.notion.scaffoldSkipped) flags.push('Notion sin configurar (revisar manualmente)');
      if (res.notion.error) flags.push(`Notion falló: ${res.notion.error}`);
      if (flags.length) {
        console.info('[admin onboarding]', flags.join(' · '));
      }
      navigate(`/businessUnit-config/${res.businessUnit.id}`);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? typeof (err as { data?: unknown }).data === 'string'
            ? (err as { data: string }).data
            : JSON.stringify((err as { data: unknown }).data)
          : 'Error inesperado al crear la BU';
      setSubmitError(message);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: SURFACE, py: { xs: 3, md: 6 }, px: { xs: 2, lg: 4 } }}>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: YELLOW,
              mb: 1,
            }}
          >
            Setup admin · Relif
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              color: NAVY,
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
            }}
          >
            Nueva Business Unit
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.9375rem',
              color: alpha(NAVY, 0.6),
              mt: 1,
              maxWidth: 560,
            }}
          >
            Da de alta una BU bajo una compañía existente o crea una compañía nueva con su setup en Notion.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
            handleSubmit(e).catch((err: unknown) => {
              console.error(err);
            });
          }}
          sx={{
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW,
            p: { xs: 3, md: 4 },
          }}
        >
          {submitError && (
            <Box sx={{ mb: 3 }}>
              <InlineFeedback severity="error">{submitError}</InlineFeedback>
            </Box>
          )}

          <FormSection ghost title="La nueva BU" description="Cómo se llama y qué tipo de operación maneja.">
            <FormGrid columns={2}>
              <FormField
                label="Nombre de la BU"
                required
                value={businessUnitName}
                onChange={(e) => setBusinessUnitName(e.target.value)}
                error={errors.businessUnitName}
              />
              <PhoneField
                label="Teléfono de la BU"
                optional
                hint="Si lo dejas vacío usamos un placeholder hasta que lo conectes."
                value={businessUnitPhoneNumber}
                onChange={setBusinessUnitPhoneNumber}
              />
            </FormGrid>
            <Box sx={{ mt: 2.5 }}>
              <FormField
                label="Tipo de operación"
                required
                select
                value={type}
                onChange={(e) => setType(e.target.value as AdminOnboardingType)}
                error={errors.type}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Box>
                      <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, color: NAVY }}>
                        {opt.label}
                      </Typography>
                      <Typography
                        sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.8125rem', color: alpha(NAVY, 0.55) }}
                      >
                        {opt.helper}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </FormField>
            </Box>
          </FormSection>

          <FormSection ghost title="Compañía" description="¿A qué compañía pertenece esta BU?">
            <Box
              role="radiogroup"
              aria-label="Tipo de compañía"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.5,
                mb: 2.5,
              }}
            >
              {[
                {
                  value: false,
                  label: 'Compañía nueva',
                  helper: 'Crea la compañía y configura su Notion.',
                },
                {
                  value: true,
                  label: 'Compañía existente',
                  helper: 'Asocia la BU a una compañía ya en Relif.',
                },
              ].map((opt) => {
                const selected = existingCompany === opt.value;
                return (
                  <Box
                    key={opt.label}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={0}
                    onClick={() => {
                      setExistingCompany(opt.value);
                      setErrors((prev) => ({ ...prev, companyName: undefined, companyId: undefined }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExistingCompany(opt.value);
                        setErrors((prev) => ({ ...prev, companyName: undefined, companyId: undefined }));
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: selected ? alpha(YELLOW, 0.18) : SURFACE_CONTAINER_LOWEST,
                      borderRadius: RADIUS_ITEM,
                      p: 2,
                      boxShadow: selected ? `inset 0 0 0 2px ${NAVY}` : `inset 0 0 0 1px ${alpha(NAVY, 0.1)}`,
                      transition: 'box-shadow 0.18s ease, background-color 0.18s ease',
                      '&:hover': {
                        boxShadow: selected ? `inset 0 0 0 2px ${NAVY}` : `inset 0 0 0 1px ${alpha(NAVY, 0.25)}`,
                      },
                      '&:focus-visible': {
                        outline: 'none',
                        boxShadow: `inset 0 0 0 2px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.35)}`,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        fontWeight: 600,
                        color: NAVY,
                        fontSize: '0.9375rem',
                      }}
                    >
                      {opt.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        color: alpha(NAVY, 0.6),
                        fontSize: '0.8125rem',
                        mt: 0.25,
                      }}
                    >
                      {opt.helper}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {existingCompany ? (
              <Stack spacing={2.5}>
                <Autocomplete
                  options={sortedCompanies}
                  loading={isLoadingCompanies}
                  getOptionLabel={(opt) => opt.name}
                  value={selectedCompany}
                  onChange={(_, value) => setSelectedCompanyId(value?.id ?? null)}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Buscar compañía..."
                      size="small"
                      error={!!errors.companyId}
                      helperText={errors.companyId ?? 'Selecciona la compañía a la que se asociará la BU.'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: RADIUS_ITEM,
                          bgcolor: SURFACE_CONTAINER_LOWEST,
                          fontFamily: 'Jost, sans-serif',
                          '& fieldset': { borderColor: alpha(NAVY, 0.12) },
                          '&:hover fieldset': { borderColor: alpha(NAVY, 0.25) },
                          '&.Mui-focused fieldset': { borderColor: NAVY, borderWidth: 1.5 },
                        },
                        '& .MuiFormHelperText-root': { color: alpha(NAVY, 0.55), mx: 0, mt: 0.625 },
                      }}
                    />
                  )}
                />

                <Tooltip title={isDev ? 'Notion deshabilitado en development' : ''} placement="top-start">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={effectiveScaffold}
                        disabled={scaffoldDisabled}
                        onChange={(_, checked) => setScaffoldNotionForBu(checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 500 }}>
                          Crear página de la BU en Notion
                        </Typography>
                        <Typography
                          sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.55), fontSize: '0.8125rem' }}
                        >
                          {scaffoldHelper}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                </Tooltip>
              </Stack>
            ) : (
              <Stack spacing={2.5}>
                <FormField
                  label="Nombre de la compañía"
                  required
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  error={errors.companyName}
                />
                <FormField
                  label="Dominios de email"
                  optional
                  hint="Separa varios con coma. Ej: empresa.cl, empresa.com"
                  multiline
                  minRows={2}
                  value={domainsRaw}
                  onChange={(e) => setDomainsRaw(e.target.value)}
                />
                <FormField
                  label="ID del grupo de WhatsApp"
                  optional
                  hint="Formato JID. Ej: 1234567890@g.us"
                  value={whatsappGroupJid}
                  onChange={(e) => setWhatsappGroupJid(e.target.value)}
                />

                <Tooltip title={isDev ? 'Notion deshabilitado en development' : ''} placement="top-start">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={effectiveSetupNotion}
                        disabled={isDev}
                        onChange={(_, checked) => setSetupNotion(checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 500 }}>
                          Setup automático en Notion
                        </Typography>
                        <Typography
                          sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.55), fontSize: '0.8125rem' }}
                        >
                          {setupNotionHelper}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                </Tooltip>
              </Stack>
            )}
          </FormSection>

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={1.5}
            sx={{ mt: 1, justifyContent: 'flex-end' }}
          >
            <BackButton onClick={() => navigate('/admin')} disabled={isLoading}>
              Cancelar
            </BackButton>
            <PrimaryButton type="submit" disabled={isLoading}>
              {isLoading ? 'Creando…' : 'Crear BU'}
            </PrimaryButton>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminOnboarding;
